import { ChevronLeft as ChevronLeftIcon } from '@mui/icons-material';
import { Box, Button, Tooltip, Typography, useTheme } from '@mui/material';
import React, { useMemo } from 'react';

import { Price } from '../../../components';
import { BackorderChip } from '../../../components/_mui';
import { PREORDER_IS_ENABLED_FOR_CAMPAIGN_MESSAGE } from '../../../constants/inventories';
import { useCurrency } from '../../../contexts/CurrencyProvider';
import { ICampaignItem } from '../../../types/campaigns';
import { IOnDemandItem } from '../../../types/inventories';
import { IReportEngagementItem } from '../../../types/reports';
import { hasPreorderAllowed } from '../../../utils/inventories';

interface IProps {
  items: (ICampaignItem | IOnDemandItem | IReportEngagementItem)[];
  children: React.ReactNode;
  isEditSendButtonVisible?: boolean;
  onReset?: () => void;
  isDirectSend?: boolean;
  handleEditSendClick: () => void;
}

const buttonStyles = { fontSize: '15px', lineHeight: 1.7, py: 1, px: 2.75 };
const textStyles = { fontSize: '16px', lineHeight: 1.4 };

export const SidebarFooter: React.FC<IProps> = ({
  items,
  children,
  isEditSendButtonVisible,
  onReset,
  isDirectSend,
  handleEditSendClick,
}) => {
  const { sass } = useTheme();
  const { getCampaignTotal } = useCurrency();

  const boxDisplayedPrice = useMemo(() => getCampaignTotal({ items }), [getCampaignTotal, items]);

  const actionButtons = useMemo(() => {
    return React.Children.toArray(children).slice(0, 2);
  }, [children]);

  const hasOneActionButton = useMemo(() => actionButtons.length === 1, [actionButtons]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        bottom: 0,
        px: 3,
        pt: 2,
        backgroundColor: sass.white,
      }}
    >
      <Box>
        {!hasOneActionButton ? actionButtons[0] : null}
        {isEditSendButtonVisible && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleEditSendClick}
            data-action="edit_campaign_btn"
            sx={buttonStyles}
          >
            Edit send
          </Button>
        )}
        {onReset && !isDirectSend && (
          <Button
            variant="outlined"
            size="small"
            onClick={onReset}
            data-action="back_btn"
            sx={buttonStyles}
            startIcon={<ChevronLeftIcon />}
          >
            Back
          </Button>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {hasPreorderAllowed(items) && (
            <Tooltip title={PREORDER_IS_ENABLED_FOR_CAMPAIGN_MESSAGE} placement="top" arrow>
              <BackorderChip />
            </Tooltip>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ ...textStyles }}>
            Total price
          </Typography>
          <Typography variant="body2" fontWeight="medium" color="text.primary" sx={{ ...textStyles }}>
            <Price value={boxDisplayedPrice} />
          </Typography>
        </Box>
        <Box>{hasOneActionButton ? actionButtons[0] : actionButtons[1]}</Box>
      </Box>
    </Box>
  );
};
